import { inject, observer } from 'mobx-react';
import React from 'react';
import { Card } from 'react-bootstrap';
import { withRouter, RouteComponentProps } from 'react-router-dom';

import Store from '../../../store';
import Error from '../../components/error/Error';
import LeadSampleTable, {
  COLUMN_VISIBILITY_BAG,
  DEFAULT_COLUMN_VISIBILITY,
} from '../../components/model/LeadSampleTable';
import Spinner from '../../components/Spinner';
import { CustomerFitSampleLead } from '../../../models/SampleLead';

interface SpotCheckPageProps extends RouteComponentProps {
  store?: Store;
}

interface SpotCheckPageState {
  falseNegativesFormCheck: boolean;
  isLoading: boolean;
  columnVisibility: Partial<Record<keyof CustomerFitSampleLead, boolean>>;
}

/**
 * The sample on its own route, which is what ships when the revamp flag is off.
 * The table itself moved to `LeadSampleTable` so the merged Signals page can
 * show the same one; nothing else here changed.
 */

const SpotCheckPage = inject('store')(
  observer(
    class SpotCheckPage extends React.Component<
      SpotCheckPageProps,
      SpotCheckPageState
    > {
      constructor(props: SpotCheckPageProps) {
        super(props);
        this.handleGetLeadsSample = this.handleGetLeadsSample.bind(this);
        this.handleFalseNegativesFormCheckChange =
          this.handleFalseNegativesFormCheckChange.bind(this);

        this.state = {
          falseNegativesFormCheck: false,
          isLoading: true,
          columnVisibility: DEFAULT_COLUMN_VISIBILITY,
        };
      }

      async componentDidMount() {
        const savedColumnVisibility =
          localStorage.getItem(COLUMN_VISIBILITY_BAG) ?? null;
        if (savedColumnVisibility) {
          this.setState({
            columnVisibility: JSON.parse(savedColumnVisibility),
          });
        }

        await this.props.store.loadValidationData({ isProcessing: true });
        await this.handleGetLeadsSample();
        this.setState({ isLoading: false });
      }

      async handleGetLeadsSample() {
        this.props.store.analyticsTrackEvent(
          'Requested a new dataset to spot check'
        );
        await this.props.store.getSpotCheckLeads(
          this.state.falseNegativesFormCheck
        );
      }

      handleFalseNegativesFormCheckChange() {
        this.setState(
          {
            falseNegativesFormCheck: !this.state.falseNegativesFormCheck,
          },
          async () => {
            await this.handleGetLeadsSample();
          }
        );
      }

      render() {
        const { validationPage, spotCheckPage } = this.props.store;
        const { leadsSpotCheck, loadSpotCheckError } = spotCheckPage;
        const {
          validation: { error: validationError },
        } = validationPage;

        if (this.state.isLoading) {
          return <Spinner />;
        }

        return (
          <div className="mt-3 mb-5">
            {(validationError || loadSpotCheckError) && (
              <Error message={validationError ?? loadSpotCheckError} />
            )}
            {/* The card and its explanation only once there is a sample to
                explain. `LeadSampleTable` returns null without one, so without
                this the page draws an empty card around nothing. */}
            {leadsSpotCheck && (
              <Card>
                <Card.Body>
                  <p>
                    Here is a list of 100 random leads, from the validation
                    dataset, scored with this model to let you review the output
                    of the model.
                  </p>
                  <LeadSampleTable
                    columnVisibility={this.state.columnVisibility}
                    falseNegativesOnly={this.state.falseNegativesFormCheck}
                    onToggleFalseNegatives={
                      this.handleFalseNegativesFormCheckChange
                    }
                    onRefresh={this.handleGetLeadsSample}
                  />
                </Card.Body>
              </Card>
            )}
          </div>
        );
      }
    }
  )
);

export default withRouter(SpotCheckPage);
